import { useEffect, useState } from "react";
import { Download, Edit, Eye, Loader2, Search, Trophy } from "lucide-react";

import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { VideoPlayerModal } from "@/projects/components/video-player-modal";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/projects/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import type { Submission } from "@/projects/lib/store";
import {
  fetchStudentVideoSubmissionsPage,
  fetchStudentVideoSubmissionsRawAll,
  getSubmissionApiErrorMessage,
  setSubmissionRewardNumber,
  STATUS_API_VALUE,
  type ApiStudentVideoSubmission,
} from "@/projects/lib/submissionsApi";
import { toast } from "@/projects/lib/toast";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";

const escapeCsvValue = (value: unknown) => {
  const text = value === null || value === undefined ? "" : String(value);
  const escaped = text.replace(/"/g, '""');
  if (/[",\n\r]/.test(escaped)) {
    return `"${escaped}"`;
  }
  return escaped;
};

const formatRawValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export function RewardPage() {
  const [editing, setEditing] = useState<Submission | null>(null);
  const [videoOpen, setVideoOpen] = useState<Submission | null>(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => window.clearTimeout(t);
  }, [q]);

  const handleExport = async (scope: "all" | "filtered" | "rewarded") => {
    let rows: ApiStudentVideoSubmission[];
    const exportFilters = {
      status: STATUS_API_VALUE["Ready for Reward"],
      search: scope === "filtered" ? debouncedQ || undefined : undefined,
    };

    setExporting(true);
    try {
      rows = await fetchStudentVideoSubmissionsRawAll(exportFilters);
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ||
        "Failed to export submissions."
      );
      return;
    } finally {
      setExporting(false);
    }

    if (scope === "rewarded") {
      rows = rows.filter(row => {
        const val = row.reward_number ?? row.reward;
        return val !== null && val !== undefined && val !== "";
      });
    }

    if (!rows.length) {
      toast.error("No records to export.");
      return;
    }

    const excludedFields = new Set([
      "video_file_size",
      "original_file_size",
      "compressed_file_size",
      "was_compressed",
      "upload_error",
      "assigned_teacher",
      "id",
    ]);

    const headers: string[] = [];
    const seenHeaders = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!seenHeaders.has(key) && !excludedFields.has(key)) {
          seenHeaders.add(key);
          headers.push(key);
        }
      }
    }

    const csv = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map(row =>
        headers
          .map(key => escapeCsvValue(formatRawValue(row[key])))
          .join(",")
      ),
    ].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ready-for-reward-${scope}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed successfully");
  };

  const pagination = useServerPagination(
    ({ page, pageSize }) =>
      fetchStudentVideoSubmissionsPage(
        {
          status: STATUS_API_VALUE["Ready for Reward"],
          search: debouncedQ || undefined,
        },
        page,
        pageSize
      ),
    [debouncedQ],
    {
      onError: error =>
        toast.error(
          getSubmissionApiErrorMessage(error, "Failed to load reward list.")
        ),
    }
  );
  const rows = pagination.items;
  const loading = pagination.loading;

  return (
    <DashboardLayout
      role="admin"
      title="Ready for Reward"
      breadcrumb={["Home", "Ready for Reward"]}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name, ID, title, reference…"
              className="w-64 pl-8"
            />
          </div>
          <div className="text-muted-foreground text-sm">
            {loading
              ? "Loading…"
              : `${pagination.meta.count} video${pagination.meta.count === 1 ? "" : "s"} ready for reward`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Select
              disabled={exporting || loading}
              onValueChange={v =>
                void handleExport(v as "all" | "filtered" | "rewarded")
              }
            >
              <SelectTrigger className="w-[170px]">
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                <SelectValue placeholder="Export to Excel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="rewarded">Reward No. Records</SelectItem>
                <SelectItem value="filtered">Filtered records</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pagination.reload()}
            disabled={loading || exporting}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </div>
      </div>

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Reference</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Video Title</TableHead>
                <TableHead>Teacher</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-center">Reward No.</TableHead>
                <TableHead className="w-[1%] text-right whitespace-nowrap">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading videos…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-16 text-center">
                    <div className="mx-auto max-w-sm">
                      <div className="text-sm font-medium">
                        No videos are ready for reward yet.
                      </div>
                      <div className="text-muted-foreground text-xs">
                        Videos appear here once they are marked “Ready for
                        Reward”.
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map(s => (
                  <TableRow key={s.id} className="hover:bg-muted/40">
                    <TableCell className="text-royal font-mono text-xs font-medium">
                      {s.id}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{s.studentName}</div>
                      <div className="text-muted-foreground text-xs">
                        {s.studentId}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="truncate font-medium">{s.videoTitle}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.assignedTeacherName?.trim() ? (
                        <>
                          <div className="text-foreground font-medium">
                            {s.assignedTeacherName}
                          </div>
                          <div className="text-muted-foreground">
                            {s.verifiedAt &&
                              new Date(s.verifiedAt).toLocaleDateString()}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground italic">
                          Not Assigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {new Date(s.submittedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-center">
                      {s.rewardNumber !== undefined ? (
                        <span className="bg-success/10 text-success inline-flex min-w-8 items-center justify-center rounded-md px-2 py-0.5 text-sm font-semibold">
                          {s.rewardNumber}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="w-[1%] text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="w-[70px] justify-center px-2"
                          onClick={() => setVideoOpen(s)}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" /> View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-[70px] justify-center px-2"
                          onClick={() => setEditing(s)}
                        >
                          <Edit className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {!loading && rows.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="videos" />
        )}
      </div>

      <RewardNumberDialog
        submission={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          pagination.reload();
        }}
      />

      <VideoPlayerModal
        submission={videoOpen}
        open={!!videoOpen}
        onOpenChange={o => !o && setVideoOpen(null)}
      />
    </DashboardLayout>
  );
}

function RewardNumberDialog({
  submission,
  onClose,
  onSaved,
}: {
  submission: Submission | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (submission) {
      setValue(
        submission.rewardNumber !== undefined
          ? String(submission.rewardNumber)
          : ""
      );
    }
  }, [submission]);

  const handleSave = async () => {
    if (!submission) {
      return;
    }
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(parsed) || parsed < 0) {
      toast.error("Please enter a valid reward number.");
      return;
    }

    setSaving(true);
    try {
      await setSubmissionRewardNumber(
        submission.apiId || submission.id,
        parsed
      );
      toast.success("Reward number updated");
      onSaved();
    } catch (error) {
      toast.error(
        getSubmissionApiErrorMessage(error, "Failed to update reward number.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!submission} onOpenChange={o => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="text-success h-5 w-5" /> Edit Reward Number
          </DialogTitle>
        </DialogHeader>

        {submission && (
          <div className="space-y-4">
            <div className="bg-muted/40 rounded-lg border p-3 text-sm">
              <div className="font-medium">{submission.studentName}</div>
              <div className="text-muted-foreground text-xs">
                {submission.videoTitle} · {submission.id}
              </div>
            </div>

            <div>
              <Label htmlFor="reward-number">Reward Number</Label>
              <Input
                id="reward-number"
                type="number"
                min={0}
                inputMode="numeric"
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="Enter reward number"
                disabled={saving}
                autoFocus
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleSave();
                  }
                }}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
